import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ListPlus, ChevronDown, Plus, X, Loader2 } from "lucide-react";
import { usePersistedOpen } from "@/lib/usePersistedOpen";

const URL = `${process.env.REACT_APP_BACKEND_URL}/api/admin/stocks`;
const cfg = { withCredentials: true };
const field = "h-8 px-2 text-xs font-mono bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary focus:outline-none focus:border-bt-border-focus";

const AddForm = ({ sectors, onAdded }) => {
  const [symbol, setSymbol] = useState("");
  const [sector, setSector] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(URL, { symbol, sector: sector || undefined }, cfg);
      onAdded(data);
      toast.success(`Added ${data.symbol} · ${data.name} (${data.sector})`);
      setSymbol("");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add ticker"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-2 mb-3" data-testid="universe-add-form">
      <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="Ticker, e.g. COIN" required
        className={`${field} w-36`} data-testid="universe-add-symbol" />
      <select value={sector} onChange={(e) => setSector(e.target.value)} className={field} data-testid="universe-add-sector">
        <option value="">Auto-detect sector</option>
        {sectors.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <button type="submit" disabled={busy || !symbol} data-testid="universe-add-btn"
        className="h-8 px-3 flex items-center gap-1 text-xs font-mono bg-bt-gold text-bt-bg hover:bg-bt-gold/80 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add ticker
      </button>
    </form>
  );
};

const Chip = ({ s, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 border border-bt-border-subtle bg-bt-surface-2 text-[11px] font-mono" data-testid={`universe-chip-${s.symbol}`}
    title={`${s.name}${s.price ? ` · $${s.price.toLocaleString()}` : " · awaiting price"}`}>
    <span className={s.price ? "text-bt-text-primary" : "text-bt-text-tertiary"}>{s.symbol}</span>
    <button onClick={() => onRemove(s)} disabled={s.held} title={s.held ? "Held by Woodrow, can't remove yet" : "Remove"}
      className="p-0.5 text-bt-text-tertiary hover:text-bt-negative disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`universe-remove-${s.symbol}`}>
      <X className="w-3 h-3" />
    </button>
  </span>
);

export default function StockUniversePanel() {
  const [open, setOpen] = usePersistedOpen("stock_universe", false);
  const [data, setData] = useState(null);

  useEffect(() => { if (open && !data) axios.get(URL, cfg).then(r => setData(r.data)).catch(() => setData({ sectors: [], stocks: [] })); }, [open, data]);

  const grouped = useMemo(() => {
    const g = {};
    (data?.stocks || []).forEach(s => { (g[s.sector] = g[s.sector] || []).push(s); });
    return Object.entries(g);
  }, [data]);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.symbol} from the tracked list? Woodrow will stop considering it.`)) return;
    try {
      await axios.delete(`${URL}/${encodeURIComponent(s.symbol)}`, cfg);
      setData(d => ({ ...d, stocks: d.stocks.filter(x => x.symbol !== s.symbol) }));
      toast.success(`Removed ${s.symbol}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="bg-bt-surface border border-bt-border-subtle mb-6" data-testid="admin-stock-universe">
      <button onClick={() => setOpen(o => !o)} data-testid="toggle-stock-universe"
        className="w-full px-4 py-3 border-b border-bt-border-subtle flex items-center justify-between hover:bg-bt-surface-2/40 transition-colors">
        <div className="flex items-center gap-2">
          <ListPlus className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
          <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">Tracked Stocks</span>
          {data && <span className="text-[11px] font-mono text-bt-text-tertiary" data-testid="universe-count">{data.stocks.length}</span>}
        </div>
        <ChevronDown className={`w-4 h-4 text-bt-text-tertiary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-4">
          <p className="text-sm text-bt-text-secondary mb-3">
            Stocks Woodrow can trade and the Markets page shows. New tickers are checked against Finnhub and start pricing immediately.
          </p>
          {!data ? <Loader2 className="w-4 h-4 animate-spin text-bt-gold" /> : (
            <>
              <AddForm sectors={data.sectors} onAdded={(s) => setData(d => ({ ...d, stocks: [...d.stocks, { ...s, held: false }] }))} />
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {grouped.map(([sec, list]) => (
                  <div key={sec}>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1.5">{sec} · {list.length}</p>
                    <div className="flex flex-wrap gap-1.5">{list.map(s => <Chip key={s.symbol} s={s} onRemove={remove} />)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
